import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { Avatar } from '@components/avatar';
import { SchedulerConflicts } from '@components/scheduler/sidebar/conflicts';
import { cn } from '@utils/cn';
import { FilterChips } from '../filterChips';
import { matchesSearch } from '../helpers';
import { ConflictLink } from '../conflictLink';
import { ResourcePickerProps } from './types';

export const ResourcePicker = ({
  open,
  onOpenChange,
  options,
  selected,
  swapping,
  onToggle,
  addLabel,
  searchPlaceholder,
  hint,
  conflictTitle,
  disabled,
}: ResourcePickerProps) => {
  const { t } = useTranslation('app');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ qualified: true, available: true, onSchedule: false });
  const [pinned, setPinned] = useState<string[]>([]);

  const toggleOpen = (next: boolean) => {
    setSearch('');
    if (next) setPinned(selected);
    onOpenChange(next);
  };

  if (!open)
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => toggleOpen(true)}
        className="flex h-10 items-center gap-1.5 rounded-md border-[1.5px] border-neutral-300 bg-white px-2.5 text-[15px] text-neutral-400 disabled:bg-neutral-50">
        <Plus className="h-4 w-4" />
        <span className="flex-1 text-left">{addLabel}</span>
        <ChevronDown className="h-4 w-4" />
      </button>
    );

  const visible = options
    .filter(
      (el) =>
        selected.includes(el.value) ||
        pinned.includes(el.value) ||
        ((!filters.qualified || el.qualified !== false) &&
          (!filters.available || !(el.conflictDays ?? el.conflicts.length))),
    )
    .filter((el) => matchesSearch(`${el.label} ${el.sublabel || ''}`, search))
    .sort((a, b) => Number(pinned.includes(b.value)) - Number(pinned.includes(a.value)));

  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex h-10 items-center gap-2 rounded-md border-2 border-primary-500 bg-white px-2.5">
        <Search className="h-4 w-4 shrink-0 text-neutral-400" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="min-w-0 flex-1 bg-transparent text-base text-neutral-800 outline-none placeholder:text-neutral-400"
        />
      </label>
      <div className="overflow-hidden rounded-md border border-neutral-200 bg-white shadow-[0_8px_19px_rgba(20,30,45,.12)]">
        <div className="px-2 pb-1 pt-2">
          <FilterChips value={filters} onChange={setFilters} keys={['qualified', 'available']} />
        </div>
        <div className="max-h-60 overflow-y-auto overscroll-contain p-1">
          {visible.length ? (
            visible.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role={swapping ? 'option' : 'checkbox'}
                  aria-checked={checked}
                  onClick={() => onToggle(option.value)}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-2.5 rounded px-2 py-1 text-left',
                    checked && 'bg-primary-50',
                  )}>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border-[1.5px] border-neutral-400 bg-white">
                    {checked && <Check className="h-4 w-4 text-primary-500" strokeWidth={2.6} />}
                  </span>
                  <Avatar name={option.label} hideTooltip className="h-7 w-7 text-xs" />
                  <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium text-neutral-600">
                    <span className="truncate">{option.label}</span>
                    {option.onShowConflicts && !!option.conflictDays && (
                      <ConflictLink
                        compact
                        count={option.conflictDays}
                        onClick={option.onShowConflicts}
                      />
                    )}
                    {!option.onShowConflicts && !!option.conflicts.length && (
                      <SchedulerConflicts
                        name={option.label}
                        title={conflictTitle}
                        conflicts={option.conflicts}
                        iconClassName="text-sm"
                      />
                    )}
                  </span>
                  {option.sublabel && (
                    <span className="shrink-0 text-[11px] text-neutral-400">{option.sublabel}</span>
                  )}
                </button>
              );
            })
          ) : (
            <p className="px-2 py-2 text-sm text-neutral-400">
              {t('forms.default.multiSelect.empty')}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-neutral-100 py-0.5 pl-3 pr-1.5">
          <span className="text-[11px] text-neutral-400">{hint}</span>
          <button
            type="button"
            onClick={() => toggleOpen(false)}
            className="h-9 px-2 text-[13px] font-bold tracking-wide text-primary-600">
            {t('actions.close').toLocaleUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
};
